import { ADDABLE_MEMBER_RELATIONSHIPS } from '../types/memberRelationships';
import { isGender } from '../types/genderOptions';
import type { CreateMemberBody } from '../services/membersApi';

export interface MemberFormValues {
  firstName: string;
  lastName: string;
  relationship: string;
  dob: string;
  phone: string;
  email: string;
  dietaryRequirements: string;
  gender: string;
}

export const MEMBER_FORM_INITIAL: MemberFormValues = {
  firstName: '', lastName: '', relationship: '', dob: '', phone: '', email: '', dietaryRequirements: '', gender: '',
};

const ADDABLE = new Set(ADDABLE_MEMBER_RELATIONSHIPS.map((r) => r.value));
export function isAddableRelationship(value: string): boolean {
  return ADDABLE.has(value);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const clean = (s: string): string => s.trim().replace(/\s+/g, ' ');

export type MemberFormResult =
  | { ok: true; body: CreateMemberBody }
  | { ok: false; errors: Record<string, string> };

export function validateMemberForm(
  v: MemberFormValues,
  mode: 'create' | 'edit' = 'create',
): MemberFormResult {
  const errors: Record<string, string> = {};
  const firstName = clean(v.firstName);
  const lastName = clean(v.lastName);
  if (!firstName) errors.firstName = 'First name is required';
  if (!lastName) errors.lastName = 'Last name is required';
  if (!v.relationship) errors.relationship = 'Please choose a relationship';
  else if (!isAddableRelationship(v.relationship)) errors.relationship = 'Invalid relationship';
  if (mode === 'create') {
    if (!v.gender) errors.gender = 'Please choose a gender';
    else if (!isGender(v.gender)) errors.gender = 'Invalid gender';
  } else if (v.gender && !isGender(v.gender)) {
    errors.gender = 'Invalid gender';
  }
  if (v.dob && !ISO_DATE_RE.test(v.dob)) errors.dob = 'Date of birth must be yyyy-mm-dd';
  if (v.email && !EMAIL_RE.test(v.email.trim())) errors.email = 'Enter a valid email';

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const body: CreateMemberBody = { firstName, lastName, relationship: v.relationship };
  if (v.dob) body.dob = v.dob;
  if (v.phone.trim()) body.phone = v.phone.trim();
  if (v.email.trim()) body.email = v.email.trim();
  if (v.dietaryRequirements.trim()) body.dietaryRequirements = v.dietaryRequirements.trim();
  if (v.gender) body.gender = v.gender;
  return { ok: true, body };
}
